import { objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default from 'react';
import PropTypes from 'prop-types';
import { brands } from '../../tools/constants.js';

var _excluded = ["ariaDescribedBy", "autoComplete", "brand", "children", "classList", "id", "inputProps", "isDisabled", "isInvalid", "isReadOnly", "isRequired", "label", "maxLength", "name", "onBlur", "onChange", "onFocus", "onKeyDown", "onKeyUp", "theme", "type", "value"];
var propTypes = {
  /** With autocomplete on, the browser completes input values based on what the user entered before.
   * [Autocomplete Values](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete)
   */
  autoComplete: PropTypes.string.isRequired,
  /** Specify a custom id for input tag */
  id: PropTypes.string.isRequired,
  /** Floating label and placeholder text for the input */
  label: PropTypes.string.isRequired,
  /** Provided onClick handler that is called whenever the component is clicked */
  onChange: PropTypes.func.isRequired,
  /** Describe the input. Must be an ID of an element in the DOM. */
  ariaDescribedBy: PropTypes.string,
  brand: PropTypes.oneOf(brands),
  /** Use children to provide error messages and other important information along with the input field */
  children: PropTypes.node,
  /** Use this prop to extend the styling according to your needs */
  classList: PropTypes.string,
  /** Arbitrary attributes to be applied to the `input` element */
  inputProps: PropTypes.object,
  /** If true, disables the input */
  isDisabled: PropTypes.bool,
  /** Marks the input as invalid (sets aria-invalid) */
  isInvalid: PropTypes.bool,
  /** If true, makes the input read-only */
  isReadOnly: PropTypes.bool,
  /** Marks the input as required */
  isRequired: PropTypes.bool,
  /** Max character count allowed for the input. */
  maxLength: PropTypes.number,
  /** Name attribute for the input */
  name: PropTypes.string,
  /** Optionally provide an onBlur handler that is called whenever the input field loses focus */
  onBlur: PropTypes.func,
  /** Optionally provide an onFocus handler that is called whenever focus event occurs */
  onFocus: PropTypes.func,
  /** Optionally provide an onClick handler that is called whenever key up event occurs on the input field */
  onKeyDown: PropTypes.func,
  onKeyUp: PropTypes.func,
  theme: PropTypes.oneOf(['default', 'inverse']),
  /** Specify which type of input control to display */
  type: PropTypes.string,
  /** Specify the value of the input field */
  value: PropTypes.string
};
function InputField(_ref) {
  var ariaDescribedBy = _ref.ariaDescribedBy,
    autoComplete = _ref.autoComplete,
    brand = _ref.brand,
    children = _ref.children,
    _ref$classList = _ref.classList,
    classList = _ref$classList === void 0 ? '' : _ref$classList,
    id = _ref.id,
    inputProps = _ref.inputProps,
    isDisabled = _ref.isDisabled,
    isInvalid = _ref.isInvalid,
    isReadOnly = _ref.isReadOnly,
    isRequired = _ref.isRequired,
    label = _ref.label,
    maxLength = _ref.maxLength,
    name = _ref.name,
    onBlur = _ref.onBlur,
    onChange = _ref.onChange,
    onFocus = _ref.onFocus,
    onKeyDown = _ref.onKeyDown,
    onKeyUp = _ref.onKeyUp,
    _ref$theme = _ref.theme,
    theme = _ref$theme === void 0 ? 'default' : _ref$theme,
    _ref$type = _ref.type,
    type = _ref$type === void 0 ? 'text' : _ref$type,
    value = _ref.value,
    props = _objectWithoutProperties(_ref, _excluded);
  return /*#__PURE__*/React__default.createElement("div", _extends({
    className: "input-field ".concat(classList),
    "data-brand": brand || null,
    "data-testid": "input-field",
    "data-theme": theme
  }, props), /*#__PURE__*/React__default.createElement("input", _extends({
    "aria-describedby": ariaDescribedBy,
    "aria-disabled": isDisabled ? 'true' : null,
    "aria-invalid": isInvalid,
    "aria-required": isRequired ? 'true' : null,
    autoComplete: autoComplete,
    "data-filled": value ? 'true' : null,
    disabled: isDisabled,
    id: id,
    maxLength: maxLength,
    name: name || null,
    onBlur: onBlur,
    onFocus: onFocus,
    onChange: onChange,
    onKeyDown: onKeyDown,
    onKeyUp: onKeyUp,
    placeholder: label,
    readOnly: isReadOnly,
    required: isRequired,
    type: type,
    value: value
  }, inputProps)), /*#__PURE__*/React__default.createElement("label", {
    className: "float-label",
    htmlFor: id
  }, label), children);
}
InputField.propTypes = propTypes;

export { InputField as default };
//# sourceMappingURL=InputField.js.map
